import axios from "axios";
import type {
  ProductProperty,
  Refrigerant,
  CalcInputs,
  CalcResponse,
  VPFDimensions,
} from "@/types/calc";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");

export const BACKEND_URL_MISSING =
  "REACT_APP_BACKEND_URL is not set, so the app cannot reach the calculation service. "
  + "Set it in frontend/.env and restart the dev server.";

/** Base API URL ("" when REACT_APP_BACKEND_URL is unset — see apiUrl). */
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "";

/** Full URL for an API path; throws a clear error instead of calling "undefined/api". */
export function apiUrl(path: string): string {
  if (!API) throw new Error(BACKEND_URL_MISSING);
  return `${API}${path}`;
}

/** Default request timeout (ms) for quick API calls. */
export const DEFAULT_TIMEOUT_MS = 20000;
/** Timeout (ms) for slow calls — PDF generation and the plant optimiser. */
export const LONG_TIMEOUT_MS = 60000;

/** Shared axios instance — every API call goes through it (base URL, timeout,
 *  missing-backend guard). Pass `{ timeout: LONG_TIMEOUT_MS }` for PDF /
 *  optimise requests. */
export const http = axios.create({ baseURL: API, timeout: DEFAULT_TIMEOUT_MS });
http.interceptors.request.use((config) => {
  if (!API) throw new Error(BACKEND_URL_MISSING);
  return config;
});

export async function fetchProducts(): Promise<ProductProperty[]> {
  const { data } = await http.get("/products");
  return data;
}

export async function fetchRefrigerants(): Promise<Refrigerant[]> {
  const { data } = await http.get("/refrigerants");
  return data;
}

export interface ConfigResponse {
  config: Record<string, any>;
  defaults: Record<string, any>;
  unitSystem: Record<string, any>;
}

export async function fetchConfig(): Promise<ConfigResponse> {
  const { data } = await http.get("/config");
  return data;
}

// Convert the string-based form state into a numeric calculate payload
export function toPayload(inputs: CalcInputs) {
  // Strip the override marker (!) before converting to number
  const num = (v: string) => {
    const cleaned = (v || "").replace(/!/g, "").trim();
    return cleaned === "" ? NaN : Number(cleaned);
  };
  const isSecondary = inputs.refrigerantType === "Secondary";

  return {
    productName: inputs.productName,
    thermalProperty: inputs.thermalProperty,
    inletTemp: num(inputs.inletTemp),
    outletTemp: num(inputs.outletTemp),
    density: num(inputs.density),
    contactPc: num(inputs.contactPc),
    // Refrigerant type + conditional fields
    refrigerantType: inputs.refrigerantType,
    refrigerant: isSecondary ? inputs.heatTransferFluid : inputs.refrigerant,
    evapTemp: isSecondary ? null : num(inputs.evapTemp),
    heatTransferFluid: isSecondary ? inputs.heatTransferFluid : null,
    htfInletTemp: isSecondary ? num(inputs.htfInletTemp) : null,
    htfDeltaT: isSecondary ? num(inputs.htfDeltaT) : null,
    nFreezers: num(inputs.nFreezers),
    nStations: num(inputs.nStations),
    lStation: num(inputs.lStation),
    dStation: num(inputs.dStation),
    tStation: num(inputs.tStation),
    nDividers: num(inputs.nDividers),
    loadingTime: num(inputs.loadingTime),
    unloadingTime: num(inputs.unloadingTime),
    waitMisc: num(inputs.waitMisc),
    cyclesWindowHrs: num(inputs.cyclesWindowHrs),
    // Manual overrides — null when not set (empty string)
    overrideFreezeTime: inputs.overrideFreezeTime ? num(inputs.overrideFreezeTime) : null,
    overrideDefrostMin: inputs.overrideDefrostMin ? num(inputs.overrideDefrostMin) : null,
  };
}

export async function calculate(inputs: CalcInputs): Promise<CalcResponse> {
  const { data } = await http.post("/calculate", toPayload(inputs));
  return data;
}

// VPF geometry build — mirrors toPayload field names, adds VPF-specific defaults
export async function buildVPF(inputs: CalcInputs): Promise<{ ok: boolean; result?: any; errors?: string[] }> {
  const p = toPayload(inputs);
  const { data } = await http.post("/vpf/build", {
    ...p,
    thermalProperty: inputs.thermalProperty,
    extLift: true,
    freezeMethod: "empirical",
  });
  return data;
}

// ── Saved Scenarios ────────────────────────────────────────────────────────

/** Silent, automatic per-browser id (no UI). Stored once in localStorage. */
export function getClientId(): string {
  try {
    let id = localStorage.getItem("jackstone-client-id");
    if (!id) {
      id = (crypto?.randomUUID?.() ?? `c-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem("jackstone-client-id", id);
    }
    return id;
  } catch {
    return "anon";
  }
}

export async function saveScenario(payload: Record<string, any>): Promise<{ ok: boolean; scenario_id?: string }> {
  const { data } = await http.post("/scenarios", payload);
  return data;
}

export async function listScenarios(): Promise<any[]> {
  const { data } = await http.get("/scenarios");
  return data;
}

export async function getScenario(id: string): Promise<any> {
  const { data } = await http.get(`/scenarios/${id}`);
  return data;
}
